// Gate calculations shared by the gates page, its charts and the gate details modal.
// Timestamps from the API are venue-local clock strings ("YYYY-MM-DDTHH:mm..."), read the same
// way as retail: minutes after midnight of the game date, with times after midnight adding 1440.
import moment from 'moment';
import { QUEUE_TIME_GOOD_MAX } from '../Concession/concessionTheme';

// Same queue target as concessions: under 10 min is green, 10 min or more is red.
// Compared on the rounded minute, so a line, a card number and a KPI showing "10 min" always agree.
export const QUEUE_TARGET = QUEUE_TIME_GOOD_MAX;
export const isLongQueue = (minutes) => Math.round(minutes) >= QUEUE_TARGET;
export const queueColor = (minutes) => {
  if (minutes == null) return 'var(--q-text-subtle)';
  return isLongQueue(minutes) ? 'var(--q-bad)' : 'var(--q-good)';
};

// Readings older than this aren't shown as the live queue
export const LIVE_STALE_MINUTES = 10;

// "HH:mm" schedule time -> minute of the game day (before 3 AM counts as after midnight)
export const clockToMinute = (time) => {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return (h < 3 ? h + 24 : h) * 60 + m;
};

export const minuteToClock = (minute) =>
  moment().startOf('day').add(minute, 'minutes').format('h:mm A');

// Timestamp -> minute of the game day for `dateKey` ("YYYY-MM-DD")
export const timestampMinute = (timestamp, dateKey) => {
  if (!timestamp || !timestamp.includes('T')) return null;
  const [datePart, timePart] = timestamp.split('T');
  const [h, m] = timePart.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return (datePart > dateKey ? 1440 : 0) + h * 60 + m;
};

// Venue "now" as a game-day minute (after midnight counts as the same game day)
export const nowMinute = (now) => {
  const m = moment(now);
  const minute = m.hours() * 60 + m.minutes();
  return m.hours() < 3 ? minute + 1440 : minute;
};

// When a minute has more than one reading, the later one wins
const latestByMinute = (items, dateKey, pick) => {
  const byMinute = new Map();
  [...(items || [])]
    .filter((item) => item && item.timestamp)
    .sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0))
    .forEach((item) => {
      const minute = timestampMinute(item.timestamp, dateKey);
      if (minute != null) byMinute.set(minute, pick(item));
    });
  return Array.from(byMinute.entries())
    .map(([minute, value]) => ({ minute, ...value }))
    .sort((a, b) => a.minute - b.minute);
};

// Cumulative entrance/exit counts for one gate, one point per minute
export const countSeries = (gate, dateKey) =>
  latestByMinute(gate.rawData, dateKey, (item) => ({
    in: Number(item.countIn) || 0,
    out: Number(item.countOut) || 0,
  }));

// Queue readings for one gate, one point per minute
export const queueSeries = (gate, dateKey) =>
  latestByMinute(gate.queueData, dateKey, (item) => ({
    queueTime: item.queueTime == null ? null : Number(item.queueTime),
    queueCount: item.queueCount == null ? null : Number(item.queueCount),
  })).filter((point) => point.queueTime != null && !Number.isNaN(point.queueTime));

// Sum of every gate's cumulative counts, carrying each gate's last reading forward
export const combinedCountSeries = (seriesList) => {
  const minutes = Array.from(new Set(seriesList.flatMap((s) => s.map((p) => p.minute)))).sort((a, b) => a - b);
  const cursors = seriesList.map(() => 0);
  const last = seriesList.map(() => ({ in: 0, out: 0 }));
  return minutes.map((minute) => {
    seriesList.forEach((series, i) => {
      while (cursors[i] < series.length && series[cursors[i]].minute <= minute) {
        last[i] = series[cursors[i]];
        cursors[i] += 1;
      }
    });
    return {
      minute,
      in: last.reduce((sum, p) => sum + p.in, 0),
      out: last.reduce((sum, p) => sum + p.out, 0),
    };
  });
};

// Entries and exits within each interval of `size` minutes, from cumulative counts.
// Counting starts from zero at the start of the day; a counter reset never shows as negative.
export const intervalFlow = (series, size) => {
  if (!series.length) return [];
  const first = Math.floor(series[0].minute / size) * size;
  const lastMinute = series[series.length - 1].minute;
  const buckets = [];
  let prev = { in: 0, out: 0 };
  let i = 0;
  for (let start = first; start <= lastMinute; start += size) {
    let end = prev;
    while (i < series.length && series[i].minute < start + size) {
      end = series[i];
      i += 1;
    }
    buckets.push({
      start,
      end: start + size,
      in: Math.max(0, end.in - prev.in),
      out: Math.max(0, end.out - prev.out),
    });
    prev = end;
  }
  return buckets;
};

// People per minute over the last `window` minutes of readings (by time, not by number of readings)
export const recentRate = (series, field, window = 5) => {
  if (series.length < 2) return null;
  const latest = series[series.length - 1];
  let base = null;
  for (let i = series.length - 2; i >= 0; i -= 1) {
    base = series[i];
    if (latest.minute - series[i].minute >= window) break;
  }
  const minutes = latest.minute - base.minute;
  if (minutes <= 0) return null;
  return Math.max(0, (latest[field] - base[field]) / minutes);
};

// Latest queue reading, or null when there isn't one (or, live, when it's too old to trust)
export const latestQueue = (series, liveNow) => {
  if (!series.length) return null;
  const latest = series[series.length - 1];
  if (liveNow != null && liveNow - latest.minute > LIVE_STALE_MINUTES) return null;
  return { value: Math.round(latest.queueTime), minute: latest.minute };
};

// Highest queue reading, in whole minutes, and the first time it was reached.
// (Compares rounded values, so a later 20.2 doesn't replace an earlier 19.6 that also shows as 20.)
export const peakQueue = (series) =>
  series.reduce((peak, point) => {
    const value = Math.round(point.queueTime);
    return !peak || value > peak.value ? { value, minute: point.minute } : peak;
  }, null);

// Cumulative count at a minute (the last reading at or before it; zero before the first)
export const countAt = (series, minute, field) => {
  let value = 0;
  for (let i = 0; i < series.length && series[i].minute <= minute; i += 1) value = series[i][field];
  return value;
};

// Entries and exits between two minutes of the day
export const countsInWindow = (series, start, end) => ({
  in: Math.max(0, countAt(series, end, 'in') - countAt(series, start, 'in')),
  out: Math.max(0, countAt(series, end, 'out') - countAt(series, start, 'out')),
});

// Is a rate rising or falling? People per minute over the last `window` minutes of readings
// against the `window` minutes before that. Null until there's enough data for both stretches.
export const rateTrend = (series, field, window = 15) => {
  if (series.length < 2) return null;
  const end = series[series.length - 1].minute;
  if (end - series[0].minute < window * 2) return null;
  const now = Math.max(0, countAt(series, end, field) - countAt(series, end - window, field)) / window;
  const prev = Math.max(0, countAt(series, end - window, field) - countAt(series, end - window * 2, field)) / window;
  // Steady when the change is under 1 a minute or within 10% (small numbers swing by large percentages)
  const change = Math.abs(now - prev);
  const steady = change < 1 || (prev > 0 && change / prev < 0.1);
  return {
    now,
    prev,
    direction: steady ? 'flat' : now > prev ? 'up' : 'down',
    // Only give a percentage from a real base: going from 0.1 to 1 a minute isn't "+900%"
    pct: prev >= 2 ? Math.round((change / prev) * 100) : null,
  };
};

// Queue now minus the queue 15 minutes earlier (the reading at or before then), in minutes
export const queueDelta = (series, window = 15) => {
  if (series.length < 2) return null;
  const latest = series[series.length - 1];
  let earlier = null;
  for (let i = series.length - 2; i >= 0; i -= 1) {
    if (latest.minute - series[i].minute >= window) { earlier = series[i]; break; }
  }
  return earlier ? latest.queueTime - earlier.queueTime : null;
};

// Everything the page needs for one gate, worked out once.
// `window` ({ start, end } in game-day minutes) limits the counts and the peak queue to that range;
// without one, the day's totals from the API are used.
export const buildGateStats = (gate, dateKey, liveNow, window = null) => {
  const counts = countSeries(gate, dateKey);
  const queue = queueSeries(gate, dateKey);
  const windowed = window ? countsInWindow(counts, window.start, window.end) : null;
  const queueInWindow = window ? queue.filter((p) => p.minute >= window.start && p.minute <= window.end) : queue;
  return {
    gate,
    label: gate.label || `Gate ${gate.id}`,
    counts,
    queue,
    entrances: windowed ? windowed.in : gate.totalEntrances || 0,
    exits: windowed ? windowed.out : gate.totalExits || 0,
    hasQueue: queue.length > 0,
    queueNow: liveNow != null ? latestQueue(queue, liveNow) : null,
    queuePeak: peakQueue(queueInWindow),
    entryRate: liveNow != null ? recentRate(counts, 'in') : null,
    exitRate: liveNow != null ? recentRate(counts, 'out') : null,
    entryTrend: liveNow != null ? rateTrend(counts, 'in') : null,
    exitTrend: liveNow != null ? rateTrend(counts, 'out') : null,
  };
};

export const formatRate = (rate) => (rate == null ? '—' : rate >= 10 ? Math.round(rate).toLocaleString() : rate.toFixed(1));
